import { useContext, useEffect, useRef } from 'react';

import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset.ts';

import { useSnippetsState } from '../../store/snippetsStore.ts';
import { useInsertSnippet } from '../panel/useInsertSnippet.ts';

export const useInsertSnippetOnLoad = (
	editorPresetAPI?: FullPageEditorPresetAPI | null,
	collabEditInitialized?: boolean,
): void => {
	const insertSnippet = useInsertSnippet(editorPresetAPI);
	const { setQueryParams } = useRouteActions();
	const { getQueryParams } = useContext(RoutesContext);
	const hasInsertedRef = useRef(false);
	const { snippets, isLoaded } = useSnippetsState();

	useEffect(() => {
		const insertSnippetId = getSingleParam(getQueryParams(), 'insertSnippetId');
		if (
			!insertSnippetId ||
			!editorPresetAPI ||
			!collabEditInitialized ||
			!isLoaded ||
			hasInsertedRef.current
		) {
			return;
		}

		const snippet = snippets.find((s) => s.snippetId === insertSnippetId);
		if (!snippet) {
			return;
		}

		const result = insertSnippet(snippet);
		// Only `not-ready` is retriable: the editor API is still initialising and
		// a later effect run may succeed, so we leave the URL param in place.
		//
		// Every other status is terminal and must stop the retry loop:
		// - `success`    — the insert happened.
		// - `empty-body` — the snippet has no content that will ever appear.
		// - `error`      — the insert threw; auto-insert re-runs whenever an effect
		//   dependency (e.g. the snippets array) changes, and a deterministic
		//   exception (e.g. malformed ADF body) would throw again every time,
		//   producing a `reportError()` storm. Give up after the first attempt.
		if (result.status !== 'not-ready') {
			hasInsertedRef.current = true;
			setQueryParams({ insertSnippetId: null }, /* replace= */ true);
		}
	}, [
		editorPresetAPI,
		collabEditInitialized,
		isLoaded,
		snippets,
		getQueryParams,
		setQueryParams,
		insertSnippet,
	]);
};
